<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useCentertelTags } from 'dashboard/composables/useCentertelTags';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const { userPermissions, isAdmin, currentChat, currentUser } = useCentertelTags();

onMounted(() => {
  store.dispatch('customRole/getCustomRole');
});

const currentRole = computed(() => store.getters.getCurrentRole);
const customRoleId = computed(() => store.getters.getCurrentCustomRoleId);
const customRoles = computed(() => store.getters['customRole/getCustomRoles'] || []);
const customRoleName = computed(() => {
  if (!customRoleId.value) return null;
  const role = customRoles.value.find(r => r.id === customRoleId.value);
  return role?.name || null;
});
const isSupervisor = computed(() => currentRole.value === 'agent' && customRoleName.value === 'Supervisor');
const isVendor = computed(() => currentRole.value === 'agent' && !isSupervisor.value);

const hasAssignee = computed(() => !!currentChat.value?.meta?.assignee);
const conversationStatus = computed(() => currentChat.value?.status);

// ── Tipo de overlay ──
// Detectar supervisor via body class (CentertelToolbar já seta ct-supervisor)
const isSupervisorByClass = computed(() => document.body.classList.contains('ct-supervisor'));

const overlayType = computed(() => {
  if (!currentChat.value?.id) return null;
  if (isAdmin.value) return null;
  if (isSupervisor.value || isSupervisorByClass.value) return null;

  if (conversationStatus.value === 'resolved') {
    return 'closed-vendor';
  }
  if (!hasAssignee.value) {
    return 'queue';
  }
  // Conversa atribuída a outro vendedor — bloquear
  const assigneeId = currentChat.value?.meta?.assignee?.id;
  if (assigneeId && assigneeId !== currentUser.value?.id) {
    return 'not-mine';
  }
  return null;
});

const assigneeName = computed(() => currentChat.value?.meta?.assignee?.name || 'Outro atendente');
const assigneeAvatar = computed(() => currentChat.value?.meta?.assignee?.thumbnail || currentChat.value?.meta?.assignee?.avatar_url || '');
const assigneeInitial = computed(() => assigneeName.value.charAt(0).toUpperCase());

const contactName = computed(() => {
  return currentChat.value?.meta?.sender?.name || 'Lead';
});
const contactAvatar = computed(() => {
  return currentChat.value?.meta?.sender?.avatar || currentChat.value?.meta?.sender?.thumbnail || '';
});
const contactInitial = computed(() => {
  const name = contactName.value;
  return name ? name.charAt(0).toUpperCase() : '?';
});

// ── Supervisores (para overlay not-mine) ──
const supervisors = computed(() => {
  const allAgents = store.getters['agents/getAgents'] || [];
  const roles = store.getters['customRole/getCustomRoles'] || [];
  const supervisorRole = roles.find(r => r.name === 'Supervisor');
  if (!supervisorRole) return allAgents.filter(a => a.role === 'administrator');
  return allAgents.filter(a => a.custom_role_id === supervisorRole.id || a.role === 'administrator');
});
const selectedSupervisorId = ref(null);
const notMineMessage = ref('');

const sendNoteToSupervisor = async () => {
  if (!notMineMessage.value.trim() || !selectedSupervisorId.value) return;
  isSending.value = true;
  try {
    const supervisor = supervisors.value.find(s => s.id === selectedSupervisorId.value);
    if (!supervisor) { useAlert('Supervisor nao encontrado'); return; }
    const mentionTag = `[@${supervisor.name}](mention://user/${supervisor.id}/${encodeURIComponent(supervisor.name)})`;
    await store.dispatch('sendMessage', {
      conversationId: currentChat.value.id,
      message: `${mentionTag} ${notMineMessage.value.trim()}`,
      private: true,
    });
    notMineMessage.value = '';
    selectedSupervisorId.value = null;
    useAlert('Nota enviada ao supervisor!');
  } catch {
    useAlert('Erro ao enviar');
  } finally {
    isSending.value = false;
  }
};

// ── Menção (overlay fechado) ──
const mentionText = ref('');
const isSending = ref(false);
const selectedMentionId = ref(null);

// Agentes mencionáveis: exclui cargo Vendedor, inclui Supervisor/Admin
const mentionableAgents = computed(() => {
  const allAgents = store.getters['agents/getAgents'] || [];
  const roles = store.getters['customRole/getCustomRoles'] || [];
  const vendedorRole = roles.find(r => r.name === 'Vendedor');
  const vendedorRoleId = vendedorRole?.id;
  return allAgents.filter(a => {
    if (a.role === 'administrator') return true;
    if (vendedorRoleId && a.custom_role_id === vendedorRoleId) return false;
    return true;
  });
});

// Carregar agentes para select
const loadAgents = async () => {
  try {
    await store.dispatch('agents/get');
  } catch {
    // ignore
  }
};

const sendPrivateNote = async () => {
  if (!selectedMentionId.value) {
    useAlert('Selecione quem mencionar');
    return;
  }
  isSending.value = true;
  try {
    const agent = mentionableAgents.value.find(a => a.id === selectedMentionId.value);
    if (!agent) { useAlert('Agente não encontrado'); isSending.value = false; return; }
    const mentionTag = `[@${agent.name}](mention://user/${agent.id}/${encodeURIComponent(agent.name)})`;
    const content = mentionText.value.trim()
      ? `${mentionTag} ${mentionText.value.trim()}`
      : `${mentionTag} Solicitação de reabertura`;
    await store.dispatch('sendMessage', {
      conversationId: currentChat.value.id,
      message: content,
      private: true,
    });
    mentionText.value = '';
    selectedMentionId.value = null;
    useAlert('Menção enviada!');
  } catch {
    useAlert('Erro ao enviar');
  } finally {
    isSending.value = false;
  }
};
</script>

<template>
  <div
    v-if="overlayType"
    class="absolute inset-0 z-40 flex flex-col items-center justify-center"
    :class="overlayType !== 'not-mine' ? 'bg-white/95 backdrop-blur-sm' : ''"
    style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; pointer-events: auto;"
    @vue:mounted="loadAgents"
  >
    <!-- Lead na Fila -->
    <template v-if="overlayType === 'queue'">
      <div class="flex flex-col items-center gap-3" style="pointer-events: auto;">
        <div
          class="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center text-3xl font-bold text-amber-600 overflow-hidden"
        >
          <img
            v-if="contactAvatar"
            :src="contactAvatar"
            class="w-full h-full object-cover"
          />
          <span v-else>{{ contactInitial }}</span>
        </div>
        <p class="text-lg font-bold text-n-slate-12">Lead na Fila</p>
        <p class="text-sm text-n-slate-11">
          Aceite o lead na lista para ver as mensagens
        </p>
        <div class="bg-n-alpha-1 rounded-xl px-6 py-4 text-center">
          <p class="text-xs text-n-slate-8 mb-1">Nome</p>
          <p class="text-sm font-semibold text-n-slate-12">{{ contactName }}</p>
        </div>
      </div>
    </template>

    <!-- Conversa de outro vendedor — card flutuante sem alterar fundo -->
    <template v-if="overlayType === 'not-mine'">
      <div
        class="flex flex-col items-center gap-3 w-full max-w-[360px] px-5 py-5 rounded-2xl shadow-2xl bg-n-background border border-n-weak"
        style="pointer-events: auto;"
      >
        <!-- Avatar do atendente -->
        <div class="w-16 h-16 rounded-full bg-n-slate-3 flex items-center justify-center text-2xl font-bold text-n-slate-8 overflow-hidden border-2 border-n-weak">
          <img v-if="assigneeAvatar" :src="assigneeAvatar" class="w-full h-full object-cover" />
          <span v-else>{{ assigneeInitial }}</span>
        </div>
        <div class="text-center">
          <p class="text-base font-bold text-n-slate-12">Em atendimento com</p>
          <p class="text-sm font-semibold text-woot-500">{{ assigneeName }}</p>
        </div>
        <p class="text-xs text-n-slate-8 text-center">
          Solicite a transferencia mencionando um supervisor.
        </p>
        <!-- Select supervisor -->
        <select
          v-model="selectedSupervisorId"
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none"
          @vue:mounted="loadAgents"
        >
          <option :value="null" disabled>Marcar supervisor...</option>
          <option v-for="sup in supervisors" :key="sup.id" :value="sup.id">{{ sup.name }}</option>
        </select>
        <!-- Mensagem -->
        <textarea
          v-model="notMineMessage"
          rows="2"
          placeholder="Ex: pode transferir esse lead pra mim?"
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder:text-n-slate-8 outline-none resize-none"
        />
        <button
          :disabled="isSending || !notMineMessage.trim() || !selectedSupervisorId"
          class="w-full py-2 rounded-lg text-sm font-semibold transition-colors"
          :class="isSending || !notMineMessage.trim() || !selectedSupervisorId
            ? 'bg-n-slate-3 text-n-slate-8 cursor-not-allowed'
            : 'bg-woot-500 text-white hover:bg-woot-600'"
          @click="sendNoteToSupervisor"
        >
          {{ isSending ? 'Enviando...' : 'Enviar nota ao supervisor' }}
        </button>
      </div>
    </template>

    <!-- Atendimento Fechado (vendedor) -->
    <template v-if="overlayType === 'closed-vendor'">
      <div class="text-4xl mb-3" style="pointer-events: auto;">🔒</div>
      <p class="text-lg font-bold text-n-slate-12 mb-1">Atendimento Fechado</p>
      <p class="text-sm text-n-slate-11 mb-4">
        Mencione alguém para solicitar reabertura.
      </p>
      <div class="bg-n-alpha-1 rounded-xl px-4 py-4 w-full max-w-[400px]" style="pointer-events: auto;">
        <select
          v-model="selectedMentionId"
          class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none mb-2"
        >
          <option :value="null" disabled>Selecione quem mencionar...</option>
          <option v-for="agent in mentionableAgents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <textarea
          v-model="mentionText"
          rows="2"
          placeholder="Mensagem (opcional)..."
          class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm outline-none resize-none bg-n-background text-n-slate-12 placeholder:text-n-slate-8 focus:border-n-strong"
        />
        <button
          :disabled="isSending || !selectedMentionId"
          class="mt-2 w-full py-2 bg-woot-500 hover:bg-woot-600 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          @click="sendPrivateNote"
        >
          {{ isSending ? 'Enviando...' : 'Enviar Menção' }}
        </button>
      </div>
    </template>
  </div>
</template>
